import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useStoragePathsCache } from '../../hooks/useStoragePathsCache'
import { DisplayFieldEnum } from '../../services/document-list-view.service'
import { storagePathService } from '../../services/storage-path.service'
import { Document } from '../../types'
import { folderSegmentsFromResolvedPath, folderSegmentsFromTemplate } from '../../utils/folderTree'
import PreviewPopup from '../common/PreviewPopup'
import './DocumentHierarchyView.scss'

interface DocumentHierarchyViewProps {
  documents: Document[]
  displayFields: DisplayFieldEnum[]
  selected: Set<number>
  onToggleSelected: (document: Document, event: React.MouseEvent) => void
  onCardClick: (document: Document, event: React.MouseEvent) => void
  /** Show all defined folders, including empty ones. */
  includeAllFolders?: boolean
  /** Storage path ids active in the filter - when set, only these folders are shown. */
  activeStoragePathIds?: number[]
}

interface FolderNode {
  name: string
  fullPath: string
  documents: Document[]
  children: Map<string, FolderNode>
}

interface TreeNodeProps {
  node: FolderNode
  level: number
  expanded: Set<string>
  onToggleExpand: (path: string) => void
  selected: Set<number>
  displayFields: DisplayFieldEnum[]
  onToggleSelected: (document: Document, event: React.MouseEvent) => void
  onCardClick: (document: Document, event: React.MouseEvent) => void
  t: (key: string) => string
}

/** Finds or creates the node chain for the given segments, returns the last node. */
const ensureFolderNode = (root: FolderNode, segments: string[]): FolderNode => {
  let current = root
  let currentPath = ''

  segments.forEach((segment) => {
    currentPath = `${currentPath}/${segment}`

    if (!current.children.has(segment)) {
      current.children.set(segment, {
        name: segment,
        fullPath: currentPath,
        documents: [],
        children: new Map(),
      })
    }

    current = current.children.get(segment)!
  })

  return current
}

const countDocumentsRecursive = (node: FolderNode): number => {
  let count = node.documents.length
  node.children.forEach((child) => {
    count += countDocumentsRecursive(child)
  })
  return count
}

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  level,
  expanded,
  onToggleExpand,
  selected,
  displayFields,
  onToggleSelected,
  onCardClick,
  t,
}) => {
  const isExpanded = expanded.has(node.fullPath)
  const hasChildren = node.children.size > 0
  const hasDocuments = node.documents.length > 0
  const isPlaceholder = node.name.includes('{{') && node.name.includes('}}')
  const isEmpty = !hasChildren && !hasDocuments
  const totalDocumentsCount = countDocumentsRecursive(node)

  const handleFolderClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasChildren || hasDocuments) {
      onToggleExpand(node.fullPath)
    }
  }

  const getFolderIcon = () => {
    if (node.fullPath === '/__NO_PATH__') {
      return 'bi-folder-x'
    }
    if (isEmpty) {
      return 'bi-folder'
    }
    return isExpanded ? 'bi-folder2-open' : 'bi-folder2'
  }

  const getFolderClassName = () => {
    let className = 'document-tree-folder'
    if (node.fullPath === '/__NO_PATH__') {
      className += ' document-tree-folder-no-path'
    } else if (isEmpty) {
      className += ' document-tree-folder-empty'
    } else if (isPlaceholder) {
      className += hasDocuments ? ' document-tree-folder-placeholder-with-docs' : ' document-tree-folder-placeholder'
    } else {
      className += hasDocuments ? ' document-tree-folder-with-docs' : ' document-tree-folder-empty'
    }
    return className
  }

  return (
    <div className="document-tree-node">
      <div className={getFolderClassName()} style={{ paddingLeft: `${level * 20 + 8}px` }} onClick={handleFolderClick}>
        <div className="document-tree-folder-content">
          {hasChildren || hasDocuments ? (
            <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} document-tree-chevron`}></i>
          ) : (
            <span className="document-tree-chevron-spacer"></span>
          )}
          <i className={`bi ${getFolderIcon()} document-tree-folder-icon`}></i>
          <span className="document-tree-folder-name">{node.name}</span>
          {totalDocumentsCount > 0 && <span className="document-tree-folder-count">({totalDocumentsCount})</span>}
        </div>
      </div>

      {isExpanded && (
        <div className="document-tree-children">
          {Array.from(node.children.values()).map((child) => (
            <TreeNode
              key={child.fullPath}
              node={child}
              level={level + 1}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
              selected={selected}
              displayFields={displayFields}
              onToggleSelected={onToggleSelected}
              onCardClick={onCardClick}
              t={t}
            />
          ))}

          {hasDocuments && (
            <div className="document-tree-documents">
              {node.documents.map((document) => {
                const docTitle = document.title || document.original_file_name || t('untitled')
                const isSelected = selected.has(document.id)

                const handleDocumentClick = (e: React.MouseEvent) => {
                  e.stopPropagation()
                  onCardClick(document, e)
                }

                const handleDocumentSelect = (e: React.MouseEvent) => {
                  e.stopPropagation()
                  onToggleSelected(document, e)
                }

                return (
                  <PreviewPopup key={document.id} document={document}>
                    <div
                      className={`document-tree-document ${isSelected ? 'selected' : ''}`}
                      style={{ paddingLeft: `${(level + 1) * 20 + 8}px` }}
                      onClick={handleDocumentClick}
                    >
                      <div className="document-tree-document-content">
                        <div className="document-tree-document-checkbox" onClick={handleDocumentSelect}>
                          <i className={`bi ${isSelected ? 'bi-check-square-fill' : 'bi-square'}`}></i>
                        </div>
                        <i className="bi bi-file-earmark-fill document-tree-document-icon"></i>
                        <span className="document-tree-document-name" title={docTitle}>
                          {docTitle.length > 50 ? `${docTitle.slice(0, 47)}...` : docTitle}
                        </span>
                        {displayFields?.includes(DisplayFieldEnum.ASN) && document.archive_serial_number && (
                          <span className="document-tree-document-asn">ASN: {document.archive_serial_number}</span>
                        )}
                      </div>
                    </div>
                  </PreviewPopup>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export const DocumentHierarchyView: React.FC<DocumentHierarchyViewProps> = ({
  documents,
  displayFields,
  selected,
  onToggleSelected,
  onCardClick,
  includeAllFolders = false,
  activeStoragePathIds,
}) => {
  const { t } = useTranslation()
  const { storagePaths } = useStoragePathsCache()
  const [resolvedPaths, setResolvedPaths] = useState<Map<number, string>>(new Map())
  const [loadingPaths, setLoadingPaths] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['/']))

  useEffect(() => {
    const loadResolvedPaths = async () => {
      if (documents.length === 0 || storagePaths.length === 0) {
        setLoadingPaths(false)
        return
      }

      setLoadingPaths(true)

      const documentsWithPaths = documents.filter((doc) => doc.storage_path)

      if (documentsWithPaths.length === 0) {
        setLoadingPaths(false)
        return
      }

      try {
        const documentIds = documentsWithPaths.map((doc) => doc.id)
        const resolvedPathsMap = await storagePathService.bulkResolvePaths(documentIds)

        const pathMap = new Map<number, string>()
        for (const [docId, resolvedPath] of Object.entries(resolvedPathsMap)) {
          const documentId = parseInt(docId, 10)
          if (resolvedPath) {
            pathMap.set(documentId, resolvedPath)
          } else {
            const document = documentsWithPaths.find((doc) => doc.id === documentId)
            if (document?.storage_path) {
              const storagePathId = typeof document.storage_path === 'object' ? document.storage_path.id : document.storage_path
              const storagePath = storagePaths.find((sp) => sp.id === storagePathId)
              if (storagePath?.path) {
                pathMap.set(documentId, storagePath.path)
              }
            }
          }
        }

        setResolvedPaths(pathMap)
      } catch (error) {
        console.warn('Failed to resolve paths:', error)
        const pathMap = new Map<number, string>()
        documentsWithPaths.forEach((document) => {
          if (document.storage_path) {
            const storagePathId = typeof document.storage_path === 'object' ? document.storage_path.id : document.storage_path
            const storagePath = storagePaths.find((sp) => sp.id === storagePathId)
            if (storagePath?.path) {
              pathMap.set(document.id, storagePath.path)
            }
          }
        })
        setResolvedPaths(pathMap)
      } finally {
        setLoadingPaths(false)
      }
    }

    loadResolvedPaths()
  }, [documents, storagePaths])

  const getDocumentStoragePath = (document: Document): string => {
    if (resolvedPaths.has(document.id)) {
      return resolvedPaths.get(document.id) || ''
    }

    if (!document.storage_path) return ''
    const storagePathId = typeof document.storage_path === 'object' ? document.storage_path.id : document.storage_path
    const storagePath = storagePaths.find((sp) => sp.id === storagePathId)
    return storagePath?.path || ''
  }

  const buildFolderTree = (docs: Document[]): FolderNode => {
    const root: FolderNode = {
      name: '/',
      fullPath: '/',
      documents: [],
      children: new Map(),
    }

    // Seed from folder definitions - empty folders have no documents to derive them from.
    // An active folder filter narrows the seed to just the selected folders.
    if (includeAllFolders) {
      const foldersToSeed = activeStoragePathIds?.length
        ? storagePaths.filter((storagePath) => activeStoragePathIds.includes(storagePath.id))
        : storagePaths

      foldersToSeed.forEach((storagePath) => {
        const segments = folderSegmentsFromTemplate(storagePath.path)
        if (segments.length > 0) {
          ensureFolderNode(root, segments)
        }
      })
    }

    const documentsWithoutPath: Document[] = []

    docs.forEach((document) => {
      const path = getDocumentStoragePath(document)
      if (!path) {
        documentsWithoutPath.push(document)
        return
      }

      const segments = folderSegmentsFromResolvedPath(path)

      if (segments.length === 0) {
        documentsWithoutPath.push(document)
        return
      }

      ensureFolderNode(root, segments).documents.push(document)
    })

    if (documentsWithoutPath.length > 0) {
      root.children.set('__NO_PATH__', {
        name: t('without_storage_path'),
        fullPath: '/__NO_PATH__',
        documents: documentsWithoutPath,
        children: new Map(),
      })
    }

    return root
  }

  const tree = useMemo(() => {
    if (loadingPaths) {
      return null
    }
    // Build even with no documents - folder definitions still need to render.
    return buildFolderTree(documents)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, storagePaths, resolvedPaths, loadingPaths, includeAllFolders, activeStoragePathIds])

  const handleToggleExpand = (path: string) => {
    setExpanded((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(path)) {
        newSet.delete(path)
      } else {
        newSet.add(path)
      }
      return newSet
    })
  }

  const getAllExpandablePaths = (node: FolderNode): Set<string> => {
    const allPaths = new Set<string>(['/'])
    const collectPaths = (n: FolderNode) => {
      if (n.children.size > 0 || n.documents.length > 0) {
        allPaths.add(n.fullPath)
        n.children.forEach((child) => collectPaths(child))
      }
    }
    collectPaths(node)
    return allPaths
  }

  const isAllExpanded = () => {
    if (!tree) return false
    const allPaths = getAllExpandablePaths(tree)
    return allPaths.size === expanded.size && Array.from(allPaths).every((path) => expanded.has(path))
  }

  const handleToggleExpandAll = () => {
    if (!tree) return

    if (isAllExpanded()) {
      setExpanded(new Set(['/']))
    } else {
      const allPaths = getAllExpandablePaths(tree)
      setExpanded(allPaths)
    }
  }

  if (loadingPaths) {
    return (
      <div className="document-hierarchy-view">
        <div className="document-hierarchy-loading">
          <i className="bi bi-arrow-repeat spin me-2"></i>
          {t('loading_paths')}
        </div>
      </div>
    )
  }

  if (!tree || (tree.children.size === 0 && tree.documents.length === 0)) {
    return (
      <div className="document-hierarchy-view">
        <div className="document-hierarchy-empty">{t('no_documents')}</div>
      </div>
    )
  }

  const allExpanded = isAllExpanded()

  return (
    <div className="document-hierarchy-view">
      <div className="document-hierarchy-toolbar">
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={handleToggleExpandAll}
          title={allExpanded ? t('collapse_all') : t('expand_all')}
        >
          <i className={`bi ${allExpanded ? 'bi-arrows-angle-contract' : 'bi-arrows-angle-expand'} me-1`}></i>
          {allExpanded ? t('collapse_all') : t('expand_all')}
        </button>
      </div>
      <div className="document-hierarchy-tree">
        <TreeNode
          node={tree}
          level={0}
          expanded={expanded}
          onToggleExpand={handleToggleExpand}
          selected={selected}
          displayFields={displayFields}
          onToggleSelected={onToggleSelected}
          onCardClick={onCardClick}
          t={t}
        />
      </div>
    </div>
  )
}
